import type { EnrichedPart, PriceToleranceRule } from "../domain/types";
import {
    comparePriceToleranceImpact,
    summarizePriceToleranceImpact,
} from "../rules/priceToleranceImpact";
import type { RuleSuggestion, RuleSuggestionInput, RuleSuggestionProvider } from "./RuleSuggestionProvider";

const normalizePrompt = (prompt: string): string => prompt.trim().replace(/\s+/g, " ");

const proposalOnly = (params: {
    id: string;
    title: string;
    prompt: string;
    explanation: string;
    conditions: string[];
    actions: string[];
    affectedRows: EnrichedPart[];
}): RuleSuggestion => ({
    id: params.id,
    mode: "proposal_only",
    title: params.title,
    normalizedPrompt: normalizePrompt(params.prompt),
    confidence: "Medium",
    explanation: params.explanation,
    conditions: params.conditions,
    actions: params.actions,
    affectedRowCount: params.affectedRows.length,
    previewRows: [],
});

const applyPriceTolerancePatch = (
    rules: PriceToleranceRule[],
    manufacturerName: string,
    reviewThresholdPct: number,
): PriceToleranceRule[] =>
    rules.map((rule) => {
        if (rule.manufacturerName.toLowerCase() !== manufacturerName.toLowerCase()) return rule;

        return {
            ...rule,
            reviewThresholdPct,
            autoApproveThresholdPct: Math.min(rule.autoApproveThresholdPct, reviewThresholdPct),
            actionOnBreach: "HOLD + notify procurement",
            notes: "Inserted from Ask AppGlue demo prompt",
        };
    });

const buildPriceToleranceSuggestion = (input: RuleSuggestionInput): RuleSuggestion => {
    const manufacturerName = "Texas Instruments";
    const reviewThresholdPct = 8;
    const candidateRules = applyPriceTolerancePatch(input.currentPriceToleranceRules, manufacturerName, reviewThresholdPct);
    const previewRows = comparePriceToleranceImpact(input.data.priceUpdates, input.currentPriceToleranceRules, candidateRules, 8);

    return {
        id: "ask-price-ti-hold-8",
        mode: "executable_command",
        title: "Hold TI price increases above 8%",
        normalizedPrompt: normalizePrompt(input.prompt),
        confidence: "High",
        explanation:
            "The prompt maps to the price tolerance rule table: keep the existing auto-approve threshold, lower the review threshold to 8%, and hold matching price updates for procurement review.",
        conditions: [
            "manufacturer_name equals Texas Instruments",
            "price_change_pct is greater than 8",
            "source table is supplier_price_updates",
        ],
        actions: [
            "set review threshold to 8%",
            "hold matching updates from ERP cost sync",
            "route held rows to procurement review",
        ],
        command: {
            type: "updateRuleTableRows",
            ruleId: "TR-008",
            executableType: "RuleTableConfiguration",
            rowSelector: {
                column: "manufacturer_id",
                value: "MFR-001",
            },
            patch: {
                review_threshold_pct: reviewThresholdPct,
                action_on_breach: "HOLD + notify procurement",
            },
        },
        affectedRowCount: previewRows.length,
        previewRows,
        beforeSummary: summarizePriceToleranceImpact(input.data.priceUpdates, input.currentPriceToleranceRules),
        afterSummary: summarizePriceToleranceImpact(input.data.priceUpdates, candidateRules),
    };
};

const lifecycleValuesFromPrompt = (prompt: string): string[] => {
    const normalized = normalizePrompt(prompt).toLowerCase();
    return normalized.includes("only eol") || normalized.includes("eol only") ? ["EOL"] : ["EOL", "NRND"];
};

const buildLifecycleSuggestion = (input: RuleSuggestionInput): RuleSuggestion => {
    const values = lifecycleValuesFromPrompt(input.prompt);
    const affectedRows = input.data.parts.filter((part) => values.includes(part.lifecycleStatus));

    return {
        id: "ask-lifecycle-archive",
        mode: "executable_command",
        title: "Archive blocked lifecycle statuses",
        normalizedPrompt: normalizePrompt(input.prompt),
        confidence: "High",
        explanation:
            "The prompt maps to the existing lifecycle gate: blocked lifecycle rows are held out of active catalog publish and routed to archive.",
        conditions: [`lifecycle_status is ${values.join(" or ")}`, "source table is enriched_parts"],
        actions: ["block active publish", "write row to archived_parts", "preserve rejection reason code"],
        command: {
            type: "updateStatementExpression",
            ruleId: "DQ-003",
            executableType: "StatementRuleConfiguration",
            edit: {
                conditionId: "dq003-lifecycle-blocked-statuses",
                field: "lifecycle_status",
                values,
            },
        },
        affectedRowCount: affectedRows.length,
        previewRows: [],
    };
};

const requiredFieldsFromPrompt = (prompt: string): string[] => {
    const normalized = normalizePrompt(prompt).toLowerCase();
    if (!normalized.includes("only")) return ["package_type", "pin_count", "voltage_range", "rohs_status"];

    const fields = [
        normalized.includes("package") ? "package_type" : null,
        normalized.includes("pin") ? "pin_count" : null,
        normalized.includes("voltage") ? "voltage_range" : null,
        normalized.includes("rohs") || normalized.includes("compliance") ? "rohs_status" : null,
    ].filter((field): field is string => !!field);

    return fields.length > 0 ? fields : ["package_type", "pin_count", "voltage_range", "rohs_status"];
};

const buildCompletenessSuggestion = (input: RuleSuggestionInput): RuleSuggestion => {
    const requiredFields = requiredFieldsFromPrompt(input.prompt);
    const affectedRows = input.data.parts.filter((part) => part.hasMandatoryAttributeGap);

    return {
        id: "ask-required-attributes",
        mode: "executable_command",
        title: "Require publish-ready semiconductor attributes",
        normalizedPrompt: normalizePrompt(input.prompt),
        confidence: "High",
        explanation:
            "The prompt maps to the mandatory attribute completeness rule for semiconductor rows before they can publish downstream.",
        conditions: [
            "category equals Semiconductor",
            `${requiredFields.join(", ")} must be populated`,
        ],
        actions: ["reject incomplete rows", "route rejected rows to quarantine_records", "include missing attribute reason code"],
        command: {
            type: "updateStatementExpression",
            ruleId: "DQ-001",
            executableType: "StatementRuleConfiguration",
            edit: {
                conditionId: "dq001-category-condition",
                values: ["Semiconductor"],
                requirementId: "dq001-required-fields",
                requirementValues: requiredFields,
            },
        },
        affectedRowCount: affectedRows.length,
        previewRows: [],
    };
};

const buildComplianceSuggestion = (input: RuleSuggestionInput): RuleSuggestion =>
    proposalOnly({
        id: "ask-compliance-change",
        title: "Escalate compliance changes",
        prompt: input.prompt,
        explanation:
            "The prompt maps to a comparison rule that prevents compliance status changes from silently overwriting published values.",
        conditions: ["prior compliance status differs from current enriched value", "source table is enriched_parts"],
        actions: ["route to compliance_review_queue", "keep prior published value until reviewed", "write audit decision"],
        affectedRows: input.data.parts.filter((part) => part.hasComplianceChange),
    });

const buildConfidenceSuggestion = (input: RuleSuggestionInput): RuleSuggestion =>
    proposalOnly({
        id: "ask-confidence-threshold",
        title: "Publish only high-confidence enrichments",
        prompt: input.prompt,
        explanation:
            "The prompt maps to an enrichment confidence threshold that holds low-confidence rows for review before downstream publish.",
        conditions: ["confidence_score is at least 85 for publish", "source table is enriched_parts"],
        actions: ["promote high-confidence rows", "route low-confidence rows to pending_review"],
        affectedRows: input.data.parts.filter((part) => part.confidenceScore < 85),
    });

const buildFallbackSuggestion = (input: RuleSuggestionInput): RuleSuggestion =>
    proposalOnly({
        id: "ask-fallback",
        title: "Draft rule proposal",
        prompt: input.prompt,
        explanation:
            "The stub provider could not map this prompt to an executable demo rule yet, so it returns a review-only draft that preserves the prompt and expected validation step.",
        conditions: ["business user prompt requires review"],
        actions: ["draft rule for analyst review", "validate against generated data before activation"],
        affectedRows: [],
    });

export class StubRuleSuggestionProvider implements RuleSuggestionProvider {
    suggest(input: RuleSuggestionInput): RuleSuggestion {
        const normalized = normalizePrompt(input.prompt).toLowerCase();

        if (normalized.includes("texas instruments") && normalized.includes("price") && normalized.includes("8")) {
            return buildPriceToleranceSuggestion(input);
        }

        if (normalized.includes("lifecycle") || normalized.includes("eol") || normalized.includes("not recommended")) {
            return buildLifecycleSuggestion(input);
        }

        if (normalized.includes("package") || normalized.includes("pin count") || normalized.includes("mandatory")) {
            return buildCompletenessSuggestion(input);
        }

        if (normalized.includes("compliance") || normalized.includes("rohs")) {
            return buildComplianceSuggestion(input);
        }

        if (normalized.includes("confidence") || normalized.includes("85")) {
            return buildConfidenceSuggestion(input);
        }

        return buildFallbackSuggestion(input);
    }
}

export const stubRuleSuggestionProvider = new StubRuleSuggestionProvider();
